import {useMessage} from '@coolcolduk/ui-mui';
import {ArrowBack, Lightbulb, ReportProblem, School} from '@mui/icons-material';
import {Box, IconButton, Stack, Tooltip} from '@mui/material';
import {QuizSession, QuizTimedMode, SessionQuestion} from '@cp/model';
import {useFlagQuestionAsError} from '../../hooks/question/useFlagQuestionAsError';
import {useFlagQuestionAsOutOfScope} from '../../hooks/question/useFlagQuestionAsOutOfScope';
import SessionQuestionNavigationButton from './SessionQuestionNavigationButton';

export interface SessionQuestionNavigationProps {
  currentQuestion: SessionQuestion;
  handleHint: () => void;
  handleNextQuestion: () => void;
  handlePreviousQuestion: () => void;
  handleSubmitAnswer: () => void;
  handleEndSession: () => void;
  isAnswered: boolean;
  isFirstQuestion: boolean;
  isLastQuestion: boolean;
  session: QuizSession;
}

export default function SessionQuestionNavigation({
  currentQuestion,
  handleHint,
  handleNextQuestion,
  handlePreviousQuestion,
  handleSubmitAnswer,
  handleEndSession,
  isAnswered,
  isFirstQuestion,
  isLastQuestion,
  session,
}: SessionQuestionNavigationProps) {
  const {showMessage} = useMessage();
  const {flagQuestionAsError, loading: isFlagErrorLoading} = useFlagQuestionAsError();
  const {flagQuestionAsOutOfScope, loading: isFlagOutOfScopeLoading} = useFlagQuestionAsOutOfScope();

  const handleFlagAsError = async () => {
    if (!session || !currentQuestion) return;

    try {
      const result = await flagQuestionAsError(session.moduleId, currentQuestion.id, 'Reported by user');
      if (result) {
        showMessage('Question error reported successfully! Thank you for your feedback.', 3000, 'success');
      } else {
        showMessage('Failed to report question. Please try again.', 3000, 'error');
      }
    } catch (error) {
      console.error('Failed to flag question as error:', error);
      showMessage('Failed to report question. Please try again.', 3000, 'error');
    }
  };

  const handleFlagAsOutOfScope = async () => {
    if (!session || !currentQuestion) return;

    try {
      const result = await flagQuestionAsOutOfScope(session.moduleId, currentQuestion.id, 'Reported by user');
      if (result) {
        showMessage('Question out of scope reported successfully! Thank you for your feedback.', 3000, 'success');
      } else {
        showMessage('Failed to report question. Please try again.', 3000, 'error');
      }
    } catch (error) {
      console.error('Failed to flag question as out of scope:', error);
      showMessage('Failed to report question. Please try again.', 3000, 'error');
    }
  };

  return (
    <Box sx={{display: 'flex', gap: 2, justifyContent: 'space-between'}}>
      <Stack direction="row" gap={{xs: 0, sm: 1}}>
        {/* Navigation restrictions based on session mode */}
        {session.timedMode !== QuizTimedMode.TIMED_PER_QUESTION && (
          <Tooltip title="Previous Question">
            <span>
              <IconButton onClick={handlePreviousQuestion} disabled={isFirstQuestion}>
                <ArrowBack />
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Tooltip title="Hint">
          <span>
            <IconButton onClick={handleHint} disabled={!currentQuestion.hint}>
              <Lightbulb />
            </IconButton>
          </span>
        </Tooltip>

        {/* Only show report buttons if question doesn't already have status FLAGGED or ERROR or OUT_OF_SCOPE */}
        {!['FLAGGED', 'ERROR', 'OUT_OF_SCOPE'].includes(currentQuestion.status || '') && (
          <>
            <Tooltip title="Report Question Error">
              <span>
                <IconButton onClick={handleFlagAsError} color="warning" disabled={isFlagErrorLoading}>
                  <ReportProblem />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Report Question Out of Scope">
              <span>
                <IconButton onClick={handleFlagAsOutOfScope} color="info" disabled={isFlagOutOfScopeLoading}>
                  <School />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Stack>

      <SessionQuestionNavigationButton
        isAnswered={isAnswered}
        handleSubmitAnswer={handleSubmitAnswer}
        handleNextQuestion={handleNextQuestion}
        handleEndSession={handleEndSession}
        isLastQuestion={isLastQuestion}
      />
    </Box>
  );
}
